import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Siren, Loader2, RefreshCw, Play, AlertTriangle, AlertCircle, CheckCircle2, MinusCircle,
  Clock, SlidersHorizontal, LineChart, Save, X, Bell, Send, Bookmark, Trash2, Star,
} from 'lucide-react';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import ProcessHelp from '@/components/ProcessHelp';

const STATUS = {
  alert: { label: 'Alert', cls: 'bg-rose-500/10 border-rose-500/40 text-rose-600 dark:text-rose-300', Icon: AlertCircle },
  warning: { label: 'Warning', cls: 'bg-amber-500/10 border-amber-500/30 text-amber-600 dark:text-amber-300', Icon: AlertTriangle },
  ok: { label: 'OK', cls: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-300', Icon: CheckCircle2 },
  no_data: { label: 'No data', cls: 'bg-muted/40 border-border/40 text-muted-foreground', Icon: MinusCircle },
};
const SEV = {
  high: 'bg-rose-500/15 border-rose-500/40 text-rose-600 dark:text-rose-300',
  medium: 'bg-amber-500/15 border-amber-500/40 text-amber-600 dark:text-amber-300',
  low: 'bg-muted/40 border-border/40 text-muted-foreground',
};
const SIG_TYPES = ['loss_ratio', 'frequency', 'reserve_drift'];
const SIG_LABEL = { loss_ratio: 'Loss ratio', frequency: 'Claim frequency', reserve_drift: 'Reserve drift' };
const UNIT = { loss_ratio: 'pct', frequency: 'x', reserve_drift: 'pct' };
const SCALE_HINT = {
  loss_ratio: 'Enter as a percentage — e.g. 70 for a 70% loss ratio.',
  frequency: 'Enter as a multiple of the book average — e.g. 1.5 for 1.5×.',
  reserve_drift: 'Enter as a percentage — e.g. 15 for a 15% reserve shortfall.',
};
const PLACEHOLDER = { loss_ratio: { warn: '70', alert: '80' }, frequency: { warn: '1.5', alert: '2' }, reserve_drift: { warn: '15', alert: '30' } };

const unitLabel = (st) => (UNIT[st] === 'pct' ? '%' : '×');
const toInput = (st, v) => (v === null || v === undefined ? '' : (UNIT[st] === 'pct' ? String(Math.round(v * 1000) / 10) : String(v)));
const fromInput = (st, s) => {
  const n = parseFloat(s);
  if (Number.isNaN(n)) return null;
  return UNIT[st] === 'pct' ? n / 100 : n;
};
const interpretDisplay = (st, s) => {
  const v = fromInput(st, s);
  if (v == null) return '';
  return UNIT[st] === 'pct' ? `${Math.round(v * 1000) / 10}%` : `${v}×`;
};

const dotColor = (s) => (s === 'alert' ? '#f43f5e' : s === 'warning' ? '#f59e0b' : '#10b981');
const sid = (s) => String(s).replace(/[^a-z0-9]/gi, '-');

function Sparkline({ points }) {
  if (!points || points.length < 2) {
    return <span className="text-[10px] text-muted-foreground">Not enough history yet — daily runs build the trend.</span>;
  }
  const vals = points.map((p) => (p.value == null ? 0 : p.value));
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = max - min || 1;
  const W = 200;
  const H = 34;
  const step = W / (points.length - 1);
  const coords = points.map((p, i) => [i * step, H - (((p.value == null ? min : p.value) - min) / range) * (H - 6) - 3]);
  const d = coords.map((c, i) => `${i ? 'L' : 'M'}${c[0].toFixed(1)},${c[1].toFixed(1)}`).join(' ');
  return (
    <svg width={W} height={H} className="overflow-visible">
      <path d={d} fill="none" stroke="currentColor" strokeWidth="1.5" className="text-muted-foreground/50" />
      {coords.map((c, i) => (
        <circle key={i} cx={c[0]} cy={c[1]} r="2.5" fill={dotColor(points[i].status)}>
          <title>{`${new Date(points[i].generated_at).toLocaleDateString()} · ${points[i].display || points[i].value} · ${points[i].status}`}</title>
        </circle>
      ))}
    </svg>
  );
}

const configToForm = (config, lobs) => {
  const d = config?.defaults || {};
  const defaults = {};
  SIG_TYPES.forEach((st) => {
    defaults[st] = { warn: toInput(st, d[st]?.warn), alert: toInput(st, d[st]?.alert) };
  });
  const per_lob = {};
  (lobs || []).forEach((lob) => {
    const ov = (config?.per_lob || {})[lob] || {};
    per_lob[lob] = {
      loss_ratio: { warn: toInput('loss_ratio', ov.loss_ratio?.warn), alert: toInput('loss_ratio', ov.loss_ratio?.alert) },
      frequency: { warn: toInput('frequency', ov.frequency?.warn), alert: toInput('frequency', ov.frequency?.alert) },
    };
  });
  return { defaults, per_lob, auto_run: config?.auto_run !== false };
};

const formToPayload = (form) => {
  const defaults = {};
  SIG_TYPES.forEach((st) => {
    defaults[st] = { warn: fromInput(st, form.defaults[st].warn), alert: fromInput(st, form.defaults[st].alert) };
  });
  const per_lob = {};
  Object.entries(form.per_lob || {}).forEach(([lob, cfg]) => {
    const row = {};
    ['loss_ratio', 'frequency'].forEach((st) => {
      const w = fromInput(st, cfg[st]?.warn);
      const a = fromInput(st, cfg[st]?.alert);
      if (w != null && a != null) row[st] = { warn: w, alert: a };
    });
    if (Object.keys(row).length) per_lob[lob] = row;
  });
  return { defaults, per_lob, auto_run: form.auto_run };
};

export default function RiskSignalsPage() {
  const [params] = useSearchParams();
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState(params.get('model_id') || '');
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  const [showThr, setShowThr] = useState(false);
  const [thrData, setThrData] = useState(null);
  const [form, setForm] = useState(null);
  const [savingThr, setSavingThr] = useState(false);

  const [showHist, setShowHist] = useState(false);
  const [history, setHistory] = useState(null);
  const [loadingHist, setLoadingHist] = useState(false);

  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState('');
  const [presetBusy, setPresetBusy] = useState(false);

  const [showDigest, setShowDigest] = useState(false);
  const [digestCadence, setDigestCadence] = useState('off');
  const [digest, setDigest] = useState(null);
  const [digestBusy, setDigestBusy] = useState(false);

  useEffect(() => {
    api.getSemanticModels().then((r) => {
      const ms = r.models || r || [];
      setModels(ms);
      if (!modelId && ms.length) setModelId(ms[0].model_id);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadLatest = useCallback(async () => {
    if (!modelId) return;
    setLoading(true);
    try {
      const r = await api.getEarlyWarningLatest(modelId);
      setRun(r && r.signals ? r : null);
    } catch (e) {
      toast.error(e.message || 'Failed to load signals');
    } finally {
      setLoading(false);
    }
  }, [modelId]);

  useEffect(() => {
    loadLatest();
    setThrData(null); setForm(null); setHistory(null); setDigest(null);
  }, [loadLatest]);

  const runNow = async () => {
    if (!modelId) return;
    setRunning(true);
    try {
      const r = await api.getEarlyWarning(modelId);
      setRun(r);
      const st = r?.summary?.status;
      toast[st === 'alert' ? 'error' : st === 'warning' ? 'warning' : 'success'](
        st === 'ok' ? 'No active risk signals' : (r?.summary?.top_headline || 'Signals computed'));
      if (showHist) loadHistory();
    } catch (e) {
      toast.error(e.message || 'Failed to run signals');
    } finally {
      setRunning(false);
    }
  };

  const loadThresholds = useCallback(async () => {
    if (!modelId) return;
    try {
      const r = await api.getSignalThresholds(modelId);
      setThrData(r);
      setForm(configToForm(r.config, r.lobs));
    } catch (e) {
      toast.error(e.message || 'Failed to load thresholds');
    }
  }, [modelId]);

  const loadHistory = useCallback(async () => {
    if (!modelId) return;
    setLoadingHist(true);
    try {
      const r = await api.getSignalHistory(modelId, 30);
      setHistory(r);
    } catch (e) {
      toast.error(e.message || 'Failed to load history');
    } finally {
      setLoadingHist(false);
    }
  }, [modelId]);

  const toggleThr = () => {
    const next = !showThr;
    setShowThr(next);
    if (next && !thrData) loadThresholds();
    if (next && !presets.length) loadPresets();
  };
  const toggleHist = () => {
    const next = !showHist;
    setShowHist(next);
    if (next && !history) loadHistory();
  };
  const toggleDigest = () => {
    const next = !showDigest;
    setShowDigest(next);
    if (next) { loadDigestSettings(); if (!digest) previewDigest(); }
  };

  const setDefault = (st, key, val) => setForm((f) => ({ ...f, defaults: { ...f.defaults, [st]: { ...f.defaults[st], [key]: val } } }));
  const setLob = (lob, st, key, val) => setForm((f) => ({
    ...f, per_lob: { ...f.per_lob, [lob]: { ...f.per_lob[lob], [st]: { ...f.per_lob[lob][st], [key]: val } } },
  }));

  const saveThresholds = async () => {
    if (!modelId || !form) return;
    setSavingThr(true);
    try {
      const r = await api.saveSignalThresholds(modelId, formToPayload(form));
      toast.success('Thresholds saved — recomputing signals…');
      setThrData((d) => ({ ...(d || {}), config: r.config, saved: true }));
      setForm(configToForm(r.config, thrData?.lobs));
      await runNow();
    } catch (e) {
      toast.error(e.message || 'Failed to save thresholds (stewards only)');
    } finally {
      setSavingThr(false);
    }
  };

  const summary = run?.summary;
  const lobs = thrData?.lobs || [];

  async function loadPresets() {
    try { const r = await api.getThresholdPresets(); setPresets(r.presets || []); } catch { /* noop */ }
  }
  const savePreset = async () => {
    if (!presetName.trim() || !form) return;
    setPresetBusy(true);
    try {
      await api.saveThresholdPreset({ name: presetName.trim(), ...formToPayload(form) });
      toast.success(`Preset "${presetName.trim()}" saved`);
      setPresetName('');
      await loadPresets();
    } catch (e) { toast.error(e.message || 'Failed to save preset (stewards only)'); }
    finally { setPresetBusy(false); }
  };
  const applyPreset = async (presetId) => {
    const p = presets.find((x) => x.preset_id === presetId);
    if (!p || !modelId) return;
    setSavingThr(true);
    try {
      const r = await api.saveSignalThresholds(modelId, { defaults: p.defaults, per_lob: p.per_lob, auto_run: form?.auto_run !== false });
      toast.success(`Applied preset "${p.name}" — recomputing…`);
      setThrData((d) => ({ ...(d || {}), config: r.config, saved: true }));
      setForm(configToForm(r.config, thrData?.lobs));
      await runNow();
    } catch (e) { toast.error(e.message || 'Failed to apply preset (stewards only)'); }
    finally { setSavingThr(false); }
  };
  const deletePreset = async (presetId) => {
    try { await api.deleteThresholdPreset(presetId); toast.success('Preset deleted'); await loadPresets(); }
    catch (e) { toast.error(e.message || 'Failed to delete preset'); }
  };
  const setDefaultPreset = async (presetId, makeDefault) => {
    try {
      await api.setDefaultPreset(presetId, makeDefault);
      toast.success(makeDefault ? 'Set as org default — untuned models start here' : 'Removed org default');
      await loadPresets();
      if (thrData?.source !== 'model') loadThresholds();
    } catch (e) { toast.error(e.message || 'Failed to set default (stewards only)'); }
  };

  async function loadDigestSettings() {
    if (!modelId) return;
    try { const r = await api.getDigestSettings(modelId); setDigestCadence(r.cadence || 'off'); } catch { /* noop */ }
  }
  const changeCadence = async (cadence) => {
    const prev = digestCadence;
    setDigestCadence(cadence);
    try { await api.saveDigestSettings(modelId, cadence); toast.success(`Digest cadence set to ${cadence}`); }
    catch (e) { setDigestCadence(prev); toast.error(e.message || 'Failed to save cadence (stewards only)'); }
  };
  async function previewDigest() {
    if (!modelId) return;
    setDigestBusy(true);
    try { const r = await api.getSignalDigest(modelId, 7); setDigest(r); }
    catch (e) { toast.error(e.message || 'Failed to load digest'); }
    finally { setDigestBusy(false); }
  }
  const sendDigest = async () => {
    if (!modelId) return;
    setDigestBusy(true);
    try {
      const r = await api.sendSignalDigest(modelId, 7);
      setDigest(r.digest);
      toast.success(`Digest sent to ${r.recipients} steward(s) in-app${r.email_attempted ? ' + email' : ''}`);
    } catch (e) { toast.error(e.message || 'Failed to send digest (stewards only)'); }
    finally { setDigestBusy(false); }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="risk-signals-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-heading font-semibold flex items-center gap-2">
            <Siren className="w-5 h-5 text-rose-400" /> Early-Warning Risk Signals
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">
            Deterministic, explainable indicators computed from the model&apos;s own governed data — loss-ratio deterioration, claim-frequency spikes and adverse reserve development. Thresholds are steward-tunable per line of business; runs trend over time and refresh daily. No external AI.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ProcessHelp
            testId="risk-signals-help"
            title="Risk Signals & Threshold Presets"
            section="risk-signals"
            intro="Deterministic early-warning indicators — loss-ratio, claim-frequency and reserve-drift — computed from your own governed data. No external AI."
            steps={[
              'Pick a model and click Run signals to compute the latest indicators (runs trend over time and refresh daily).',
              'Open Thresholds to tune the warn / alert bars — a value like 0.55 means 55%.',
              'Save as preset, then star a preset to make it the organization default; untuned models inherit it.',
              'The audit line beside the default star shows who set it and when.',
              'Use History to trend signals and Digest to schedule an in-app recap. Per-line-of-business overrides are supported.',
            ]}
          />
          <select value={modelId} onChange={(e) => setModelId(e.target.value)}
            className="h-8 rounded-md border border-border/50 bg-background px-2 text-xs" data-testid="rs-model-select">
            {models.map((m) => <option key={m.model_id} value={m.model_id}>{m.name || m.model_id}</option>)}
          </select>
          <Button size="sm" variant={showThr ? 'default' : 'outline'} onClick={toggleThr} className="gap-1 text-xs h-8" data-testid="rs-thresholds-toggle">
            <SlidersHorizontal className="w-3 h-3" /> Thresholds
          </Button>
          <Button size="sm" variant={showHist ? 'default' : 'outline'} onClick={toggleHist} className="gap-1 text-xs h-8" data-testid="rs-history-toggle">
            <LineChart className="w-3 h-3" /> History
          </Button>
          <Button size="sm" variant={showDigest ? 'default' : 'outline'} onClick={toggleDigest} className="gap-1 text-xs h-8" data-testid="rs-digest-toggle">
            <Bell className="w-3 h-3" /> Digest
          </Button>
          <Button size="sm" onClick={runNow} disabled={running || !modelId} className="gap-1 text-xs h-8" data-testid="rs-run">
            {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />} Run signals
          </Button>
          <Button size="sm" variant="outline" onClick={loadLatest} className="gap-1 text-xs h-8" data-testid="rs-refresh">
            <RefreshCw className="w-3 h-3" />
          </Button>
        </div>
      </div>

      {showThr && form && (
        <div className="rounded-xl border border-border/50 bg-card/40 p-4 space-y-4" data-testid="rs-thresholds-panel">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-medium flex items-center gap-2"><SlidersHorizontal className="w-4 h-4 text-cyan-400" /> Risk-appetite thresholds</h2>
            <button onClick={() => setShowThr(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
          </div>
          {thrData?.source === 'org_default' && (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/5 px-2.5 py-1.5 text-[11px] text-amber-700 dark:text-amber-300 flex items-center gap-1.5" data-testid="rs-thr-orgdefault">
              <Star className="w-3 h-3 fill-amber-400 text-amber-400" /> Using org default preset <span className="font-medium">{thrData.preset_name}</span> — not yet saved for this model. Save &amp; recompute to make it this model&apos;s own.
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {SIG_TYPES.map((st) => (
              <div key={st} className="rounded-lg border border-border/40 bg-background/40 p-3 space-y-2" data-testid={`rs-thr-default-${st}`}>
                <div className="text-xs font-medium text-foreground">{SIG_LABEL[st]}</div>
                <p className="text-[10px] text-muted-foreground">{SCALE_HINT[st]}</p>
                <div className="flex items-center gap-2">
                  <label className="text-[10px] text-amber-500 w-14">Warn</label>
                  <Input value={form.defaults[st].warn} onChange={(e) => setDefault(st, 'warn', e.target.value)}
                    placeholder={PLACEHOLDER[st].warn} className="h-7 text-xs" data-testid={`rs-thr-${st}-warn`} />
                  <span className="text-[10px] text-muted-foreground w-14 shrink-0">{unitLabel(st)} <span className="opacity-60">{form.defaults[st].warn !== '' ? `= ${interpretDisplay(st, form.defaults[st].warn)}` : ''}</span></span>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-[10px] text-rose-500 w-14">Alert</label>
                  <Input value={form.defaults[st].alert} onChange={(e) => setDefault(st, 'alert', e.target.value)}
                    placeholder={PLACEHOLDER[st].alert} className="h-7 text-xs" data-testid={`rs-thr-${st}-alert`} />
                  <span className="text-[10px] text-muted-foreground w-14 shrink-0">{unitLabel(st)} <span className="opacity-60">{form.defaults[st].alert !== '' ? `= ${interpretDisplay(st, form.defaults[st].alert)}` : ''}</span></span>
                </div>
              </div>
            ))}
          </div>

          {lobs.length > 0 && (
            <div className="space-y-2" data-testid="rs-thr-per-lob">
              <div className="text-xs font-medium text-foreground">Per line of business (leave blank to use the default)</div>
              <div className="overflow-x-auto">
                <table className="w-full text-[11px]">
                  <thead className="text-muted-foreground">
                    <tr className="text-left">
                      <th className="py-1 pr-3 font-medium">Line of business</th>
                      <th className="py-1 px-2 font-medium">Loss ratio warn %</th>
                      <th className="py-1 px-2 font-medium">Loss ratio alert %</th>
                      <th className="py-1 px-2 font-medium">Frequency warn ×</th>
                      <th className="py-1 px-2 font-medium">Frequency alert ×</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lobs.map((lob) => (
                      <tr key={lob} className="border-t border-border/30" data-testid={`rs-thr-lob-${sid(lob)}`}>
                        <td className="py-1 pr-3 font-medium text-foreground">{lob}</td>
                        {['loss_ratio', 'frequency'].map((st) => (['warn', 'alert'].map((key) => (
                          <td key={`${st}-${key}`} className="py-1 px-2">
                            <Input value={form.per_lob[lob]?.[st]?.[key] ?? ''} placeholder="—"
                              onChange={(e) => setLob(lob, st, key, e.target.value)}
                              className="h-7 text-xs w-20" data-testid={`rs-thr-lob-${sid(lob)}-${st}-${key}`} />
                          </td>
                        ))))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-border/30" data-testid="rs-presets">
            <Bookmark className="w-3.5 h-3.5 text-cyan-400" />
            <span className="text-[11px] text-muted-foreground">Presets:</span>
            <select value="" onChange={(e) => { if (e.target.value) applyPreset(e.target.value); }}
              className="h-7 rounded-md border border-border/50 bg-background px-2 text-[11px]" data-testid="rs-preset-load">
              <option value="">Load a preset…</option>
              {presets.map((p) => <option key={p.preset_id} value={p.preset_id}>{p.name}{p.is_default ? ' ★' : ''}</option>)}
            </select>
            {presets.map((p) => (
              <span key={p.preset_id} className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] ${p.is_default ? 'border-amber-500/40 text-amber-600 dark:text-amber-300' : 'border-border/40 text-muted-foreground'}`} data-testid={`rs-preset-chip-${sid(p.name)}`}>
                <button onClick={() => setDefaultPreset(p.preset_id, !p.is_default)} title={p.is_default ? 'Org default — click to unset' : 'Set as org default'}
                  className="hover:text-amber-500" data-testid={`rs-preset-default-${sid(p.name)}`}>
                  <Star className={`w-3 h-3 ${p.is_default ? 'fill-amber-400 text-amber-400' : ''}`} />
                </button>
                <span className="text-foreground">{p.name}</span>
                <button onClick={() => deletePreset(p.preset_id)} title={`Delete "${p.name}"`} className="hover:text-rose-500" data-testid={`rs-preset-delete-${sid(p.name)}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </span>
            ))}
            <div className="flex items-center gap-1 ml-auto">
              <Input value={presetName} onChange={(e) => setPresetName(e.target.value)} placeholder="Name this preset"
                className="h-7 text-[11px] w-40" data-testid="rs-preset-name" />
              <Button size="sm" variant="outline" onClick={savePreset} disabled={presetBusy || !presetName.trim()}
                className="h-7 text-[11px] gap-1" data-testid="rs-preset-save">
                <Bookmark className="w-3 h-3" /> Save as preset
              </Button>
            </div>
            {(() => {
              const dft = presets.find((p) => p.is_default);
              return dft ? (
                <div className="basis-full text-[10px] text-muted-foreground pt-0.5" data-testid="rs-preset-default-audit">
                  <Star className="w-3 h-3 inline fill-amber-400 text-amber-400 mr-1" />
                  Org default <span className="text-amber-600 dark:text-amber-300 font-medium">{dft.name}</span>
                  {dft.default_set_by_name ? <> · set by {dft.default_set_by_name}</> : null}
                  {dft.default_set_at ? <> · {new Date(dft.default_set_at).toLocaleDateString()}</> : null}
                </div>
              ) : null;
            })()}
          </div>

          <div className="flex items-center justify-between flex-wrap gap-2 pt-1">
            <label className="flex items-center gap-2 text-[11px] text-muted-foreground">
              <input type="checkbox" checked={!!form.auto_run} onChange={(e) => setForm((f) => ({ ...f, auto_run: e.target.checked }))} data-testid="rs-thr-autorun" />
              Auto-run this model&apos;s signals daily
            </label>
            <Button size="sm" onClick={saveThresholds} disabled={savingThr} className="gap-1 text-xs h-8" data-testid="rs-thr-save">
              {savingThr ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save &amp; recompute
            </Button>
          </div>
        </div>
      )}

      {showHist && (
        <div className="rounded-xl border border-border/50 bg-card/40 p-4 space-y-3" data-testid="rs-history-panel">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-medium flex items-center gap-2"><LineChart className="w-4 h-4 text-cyan-400" /> Signal history (last 30 days)</h2>
            <button onClick={() => setShowHist(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
          </div>
          {loadingHist ? (
            <div className="flex items-center gap-2 text-muted-foreground text-xs py-4" data-testid="rs-history-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading history…</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {SIG_TYPES.map((st) => {
                const pts = (history?.series || {})[st] || [];
                const last = pts[pts.length - 1];
                return (
                  <div key={st} className="rounded-lg border border-border/40 bg-background/40 p-3 space-y-2" data-testid={`rs-hist-${st}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-foreground">{SIG_LABEL[st]}</span>
                      {last && <Badge variant="outline" className={`text-[9px] ${STATUS[last.status]?.cls}`}>{STATUS[last.status]?.label}</Badge>}
                    </div>
                    <div className="text-muted-foreground"><Sparkline points={pts} /></div>
                    {last ? (
                      <p className="text-[10px] text-muted-foreground">Latest: <span className="text-foreground font-medium">{last.display || last.value}</span> · {pts.length} run{pts.length === 1 ? '' : 's'}</p>
                    ) : (
                      <p className="text-[10px] text-muted-foreground">No runs yet.</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {showDigest && (
        <div className="rounded-xl border border-border/50 bg-card/40 p-4 space-y-3" data-testid="rs-digest-panel">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-sm font-medium flex items-center gap-2"><Bell className="w-4 h-4 text-cyan-400" /> Signal digest</h2>
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-muted-foreground">Cadence</span>
              <select value={digestCadence} onChange={(e) => changeCadence(e.target.value)}
                className="h-7 rounded-md border border-border/50 bg-background px-2 text-[11px]" data-testid="rs-digest-cadence">
                <option value="off">Off</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
              </select>
              <Button size="sm" variant="outline" onClick={previewDigest} disabled={digestBusy} className="h-7 text-[11px] gap-1" data-testid="rs-digest-preview">
                {digestBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Preview
              </Button>
              <Button size="sm" onClick={sendDigest} disabled={digestBusy} className="h-7 text-[11px] gap-1" data-testid="rs-digest-send">
                <Send className="w-3 h-3" /> Send now
              </Button>
              <button onClick={() => setShowDigest(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground">
            A summary of which lines crossed their tuned thresholds over the last 7 days, delivered to stewards in-app (and by email if your workspace has email configured).
          </p>
          {digest ? (
            digest.has_content ? (
              <div className="space-y-2" data-testid="rs-digest-content">
                <div className="text-xs text-foreground font-medium">{digest.total_firing} line(s) crossed a threshold · {digest.alerts} alert-level</div>
                {digest.sections.map((sec) => (
                  <div key={sec.signal_type} className="rounded-lg border border-border/40 bg-background/40 p-2.5" data-testid={`rs-digest-${sec.signal_type}`}>
                    <div className="text-[11px] font-medium text-foreground mb-1">{sec.title}</div>
                    <div className="space-y-1">
                      {sec.firing.map((l, i) => (
                        <div key={i} className="flex items-center justify-between text-[11px]">
                          <span className="text-foreground font-medium">{l.label}</span>
                          <span className="flex items-center gap-2">
                            <span className="text-muted-foreground">{l.threshold ? `thr ${l.threshold}` : ''}</span>
                            <span className="text-foreground font-semibold">{l.display}</span>
                            <Badge variant="outline" className={`text-[8px] ${SEV[l.severity] || SEV.low}`}>{l.severity === 'high' ? 'alert' : 'warning'}</Badge>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-muted-foreground py-2" data-testid="rs-digest-empty">No lines crossed their thresholds in the last {digest.period_days} days.</div>
            )
          ) : (
            <div className="text-xs text-muted-foreground py-2">Click Preview to see the current digest.</div>
          )}
        </div>
      )}

      {summary && (
        <div className="flex items-center gap-3 flex-wrap text-xs" data-testid="rs-summary">
          <Badge variant="outline" className={STATUS[summary.status]?.cls} data-testid="rs-overall-status">
            Overall: {STATUS[summary.status]?.label || summary.status}
          </Badge>
          <span className="text-rose-500">{summary.alert} alert</span>
          <span className="text-amber-500">{summary.warning} warning</span>
          <span className="text-emerald-500">{summary.ok} ok</span>
          {run?.generated_at && (
            <span className="text-muted-foreground flex items-center gap-1">
              <Clock className="w-3 h-3" /> {new Date(run.generated_at).toLocaleString()}
              {run.generated_by === 'system:daily' && <span className="opacity-70">(auto)</span>}
            </span>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      ) : !run || !(run.signals || []).length ? (
        <div className="text-sm text-muted-foreground py-12 text-center" data-testid="rs-empty">
          No signal run yet for this model. Click <span className="text-foreground">Run signals</span> to compute them.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="rs-signals">
          {run.signals.map((s) => {
            const st = STATUS[s.status] || STATUS.no_data;
            const StIcon = st.Icon;
            return (
              <div key={s.signal_type} className={`rounded-xl border p-4 space-y-3 ${s.status === 'alert' ? 'border-rose-500/40' : s.status === 'warning' ? 'border-amber-500/30' : 'border-border/40'} bg-card/40`}
                data-testid={`rs-signal-${s.signal_type}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h2 className="text-sm font-medium text-foreground">{s.title}</h2>
                    {s.overall && (
                      <p className="text-[11px] text-muted-foreground mt-0.5">{s.overall.label}: <span className="text-foreground font-medium">{s.overall.display}</span></p>
                    )}
                  </div>
                  <Badge variant="outline" className={`text-[9px] gap-1 ${st.cls}`} data-testid={`rs-status-${s.signal_type}`}>
                    <StIcon className="w-3 h-3" />{st.label}
                  </Badge>
                </div>
                <p className={`text-sm ${s.status === 'alert' ? 'text-rose-500' : s.status === 'warning' ? 'text-amber-500' : 'text-foreground'}`} data-testid={`rs-headline-${s.signal_type}`}>
                  {s.headline}
                </p>
                {s.thresholds?.default && (
                  <p className="text-[10px] text-muted-foreground" data-testid={`rs-thr-badge-${s.signal_type}`}>
                    Thresholds: warn {s.thresholds.default.warn_display} · alert {s.thresholds.default.alert_display}
                    {s.thresholds.per_lob ? ' · per-line overrides active' : ''}
                  </p>
                )}
                {(s.items || []).length > 0 && (
                  <div className="space-y-1" data-testid={`rs-items-${s.signal_type}`}>
                    {s.items.map((it, i) => (
                      <div key={i} className="flex items-center justify-between gap-2 text-[11px] rounded-md px-2 py-1 bg-background/40">
                        <span className="text-foreground font-medium min-w-0 truncate" title={it.threshold ? `warn ${it.threshold.warn_display} · alert ${it.threshold.alert_display}${it.threshold.source === 'lob' ? ' (tuned for this line)' : ''}` : ''}>{it.label}</span>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-muted-foreground">{it.detail}</span>
                          <span className="text-foreground font-semibold">{it.display}</span>
                          <Badge variant="outline" className={`text-[8px] ${SEV[it.severity] || SEV.low}`}>{it.severity}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-[10px] text-muted-foreground leading-relaxed">{s.explanation}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export { RiskSignalsPage };
